import { useState, useEffect } from 'react';
import { useQueries } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { payrollAPI } from '../services/api';
import { useCurrencyFormatter, useSalaryRanges } from '../hooks/usePayrollFormatters';
import { usePayrollCountry } from '../hooks/usePayrollCountry';
import { formatDateFr } from '../components/payroll/payrollExportsHelpers';
import PayrollReportsSummaryCards from '../components/payroll/PayrollReportsSummaryCards';
import PayrollReportsPeriodDetails from '../components/payroll/PayrollReportsPeriodDetails';
import PayrollReportsAnalyticsCards from '../components/payroll/PayrollReportsAnalyticsCards';
import PayrollEosbReport from '../components/payroll/PayrollEosbReport';

const GULF_COUNTRIES = ['QA', 'SA'];

const COUNTRY_LABELS = {
  TN: { flag: '🇹🇳', name: 'Tunisie', deductions: 'CNSS + IRPP', currency: 'TND' },
  MA: { flag: '🇲🇦', name: 'Maroc', deductions: 'CNSS + AMO + IR', currency: 'MAD' },
  FR: { flag: '🇫🇷', name: 'France', deductions: 'CSG/CRDS + Retraite + PAS', currency: 'EUR' },
  QA: { flag: '🇶🇦', name: 'Qatar', deductions: 'QPSA (nationaux)', currency: 'QAR' },
  SA: { flag: '🇸🇦', name: 'Arabie Saoudite', deductions: 'GOSI (nationaux)', currency: 'SAR' },
};

const PayrollReports = () => {
  const { t } = useTranslation();
  const [selectedPeriodId, setSelectedPeriodId] = useState('');

  const formatCurrency = useCurrencyFormatter();
  const salaryRanges = useSalaryRanges();
  const { countryCode } = usePayrollCountry();
  const countryMeta = COUNTRY_LABELS[countryCode] || COUNTRY_LABELS.TN;

  const [periodsQ, summaryQ, entriesQ] = useQueries({
    queries: [
      {
        queryKey: ['payroll-periods'],
        queryFn: () => payrollAPI.getPayrollPeriods({ page: 1, limit: 20 }).then((d) => d.items || []),
      },
      {
        queryKey: ['payroll-summary', selectedPeriodId],
        queryFn: () => payrollAPI.getPayrollSummary(selectedPeriodId),
        enabled: !!selectedPeriodId,
      },
      {
        queryKey: ['payroll-entries-reports', selectedPeriodId],
        queryFn: () =>
          payrollAPI.getPayrollEntries(selectedPeriodId, { limit: 100 }).then((d) => d.items || []),
        enabled: !!selectedPeriodId,
      },
    ],
  });

  const periods = periodsQ.data ?? [];
  const summary = summaryQ.data ?? null;
  const entries = entriesQ.data ?? [];
  const loading = summaryQ.isPending || entriesQ.isPending;

  useEffect(() => {
    if (periods.length > 0 && !selectedPeriodId) {
      const mostRecent = [...periods].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
      setSelectedPeriodId(mostRecent.id.toString());
    }
  }, [periods]);

  const selectedPeriod = periods.find((p) => p.id.toString() === selectedPeriodId);

  const departmentStats = (() => {
    const deptMap = {};
    entries.forEach((entry) => {
      const dept = entry.employee_department || t('payrollReports.undefinedDept');
      if (!deptMap[dept]) deptMap[dept] = { employees: 0, totalGross: 0, totalNet: 0 };
      deptMap[dept].employees++;
      deptMap[dept].totalGross += entry.gross_pay || 0;
      deptMap[dept].totalNet += entry.net_pay || 0;
    });
    return Object.entries(deptMap).map(([name, s]) => ({
      name, ...s, avgSalary: s.totalGross / s.employees,
    }));
  })();

  const salaryDistribution = (() => {
    const ranges = salaryRanges.map((r) => ({ ...r, count: 0 }));
    entries.forEach((entry) => {
      const salary = entry.net_pay || 0;
      const range = ranges.find((r) => salary >= r.min && salary < r.max);
      if (range) range.count++;
    });
    return ranges.filter((r) => r.count > 0);
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-lg font-semibold">{t('payrollReports.title')}</h3>
          <div className="flex items-center gap-2 mt-1">
            <p className="text-sm text-muted-foreground">{t('payrollReports.subtitle')}</p>
            <Badge variant="outline" className="text-xs">
              {countryMeta.flag} {countryMeta.name} — {countryMeta.deductions}
            </Badge>
          </div>
        </div>
        <Select value={selectedPeriodId} onValueChange={setSelectedPeriodId}>
          <SelectTrigger className="w-64">
            <SelectValue placeholder={t('payrollReports.selectPeriod')} />
          </SelectTrigger>
          <SelectContent>
            {periods.map((period) => (
              <SelectItem key={period.id} value={period.id.toString()}>
                {period.description || `${t('payrollReports.from')} ${formatDateFr(period.start_date)}`}
                <Badge variant="outline" className="ml-2">{period.status}</Badge>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {selectedPeriod && summary && (
        <>
          <PayrollReportsSummaryCards summary={summary} formatCurrency={formatCurrency} />
          <PayrollReportsPeriodDetails selectedPeriod={selectedPeriod} summary={summary} formatCurrency={formatCurrency} />
          <PayrollReportsAnalyticsCards
            departmentStats={departmentStats}
            salaryDistribution={salaryDistribution}
            entries={entries}
            formatCurrency={formatCurrency}
          />
          {GULF_COUNTRIES.includes(countryCode) && (
            <PayrollEosbReport periodId={selectedPeriodId} formatCurrency={formatCurrency} />
          )}
        </>
      )}

      {!selectedPeriod && (
        <Card>
          <CardContent className="text-center py-12">
            <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
            <h3 className="text-lg font-medium mb-2">{t('payrollReports.noPeriodTitle')}</h3>
            <p className="text-muted-foreground">{t('payrollReports.noPeriodDesc')}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default PayrollReports;
